import { cloneDeep, isEqual } from 'lodash-es';
import { modelManager } from '../manager/entityManager';
import { globalStores } from '../stores';
import { EParamsKeys } from '../types/editParamType';
import { getUniqueId } from '.';
import { Point2dUtils } from '../math/Point2dUtils';
import { EColumnType, IWindowData } from '../data/model/model';
import type { IFurniture } from '@/editor/data/model/furniture';
import { IPoint2d } from '../math/IPoint2d';
import { IAssemblyData } from '../data/assembly/assembly';
import { BUILD_PROPERTIES } from '../constant/buildProperties';
import { EEntityType } from '../types';
import { clampScaleOffset } from '../controller/controller2d/operateEntityController/scaleLimit';

export function getFurnitureBoxOutline(furnitureData: IFurniture, point: IPoint2d) {
  const outline: IPoint2d[] = [];

  const { width, thickness } = furnitureData;

  outline.push({ x: -width / 2 + point.x, y: -thickness / 2 + point.y });
  outline.push({ x: -width / 2 + point.x, y: thickness / 2 + point.y });
  outline.push({ x: width / 2 + point.x, y: thickness / 2 + point.y });
  outline.push({ x: width / 2 + point.x, y: -thickness / 2 + point.y });

  return outline;
}

export function getFurtureRegionBoxOutline(furnitureData: IFurniture, point: IPoint2d) {
  const outline: IPoint2d[] = [];

  const baseOutline = getFurnitureBoxOutline(furnitureData, point);
  const len = baseOutline.length;
  for (let i = 0; i < len; i++) {
    outline.push(Point2dUtils.rotatePoint(baseOutline[i], point, furnitureData.rotation));
  }

  return outline;
}

export function getAssemblyBoxOutline(assemblyData: IAssemblyData, point: IPoint2d) {
  const outline: IPoint2d[] = [];

  const { width, thickness } = assemblyData;

  outline.push({ x: -width / 2 + point.x, y: -thickness / 2 + point.y });
  outline.push({ x: -width / 2 + point.x, y: thickness / 2 + point.y });
  outline.push({ x: width / 2 + point.x, y: thickness / 2 + point.y });
  outline.push({ x: width / 2 + point.x, y: -thickness / 2 + point.y });

  return outline;
}

export function getAssemblyRegionBoxOutline(assemblyData: IAssemblyData, point: IPoint2d) {
  const outline: IPoint2d[] = [];

  const baseOutline = getAssemblyBoxOutline(assemblyData, point);
  const len = baseOutline.length;
  for (let i = 0; i < len; i++) {
    outline.push(Point2dUtils.rotatePoint(baseOutline[i], point, assemblyData.rotation));
  }

  return outline;
}

function calValueScale(oldValue: number, newValue: number) {
  return newValue / oldValue;
}

function getFurnitureSize(data: IFurniture, behavior: EParamsKeys) {
  switch (behavior) {
    case EParamsKeys.length:
      return data.thickness;
    case EParamsKeys.width:
      return data.width;
    case EParamsKeys.height:
      return data.height;
    default:
      return 0;
  }
}

/** 联动的尺寸共用一个系数，须按拖动控制柄的同一范围收敛，否则输入框只挡得住主字段 */
function clampFurnitureScale(scale: number, behaviors: EParamsKeys[], data: IFurniture) {
  const dimensions = behaviors.map((behavior) => {
    const value = getFurnitureSize(data, behavior);
    return { value, rate: value };
  });
  const sizeRange = { min: BUILD_PROPERTIES.MIN_INPUT, max: BUILD_PROPERTIES.MAX_INPUT };
  return 1 + clampScaleOffset(scale - 1, dimensions, sizeRange);
}

/**
 * @todo 只按联动字段收敛系数，主字段可能被下限顶过自身上限
 * 已越界的存量数据会被强行拉回，与拖动「不越界得更多」的规则不一致
 */
function checkDoorWindowSizeScaleIsLegal(
  scale: number,
  behaviors: EParamsKeys[],
  currentDoorWindowData: IWindowData
): number {
  let shadowScale = scale;
  const isModifyWidth = behaviors[0] === EParamsKeys.width;
  const isLinked = isModifyWidth ? behaviors.includes(EParamsKeys.height) : behaviors.includes(EParamsKeys.width);
  if (!isLinked) return shadowScale;

  if (isModifyWidth) {
    const layerHeight = globalStores.paramStore.layerHeight;
    const legalHeight = layerHeight;
    const legalScale = calValueScale(currentDoorWindowData.height, legalHeight);
    const minScale = calValueScale(currentDoorWindowData.height, BUILD_PROPERTIES.MIN_INPUT);
    shadowScale = Math.max(Math.min(legalScale, shadowScale), minScale);
    return shadowScale;
  }

  const { relativeWallId, wallType } = currentDoorWindowData.relativeWallInfo;
  const currentWallData =
    wallType === EEntityType.Wall
      ? globalStores.wallStore.getWallData(relativeWallId)
      : globalStores.separateWallStore.getSepData(relativeWallId, false);
  if (currentWallData) {
    const isWall = 'baseData' in currentWallData;
    const start = isWall ? currentWallData.baseData.start : currentWallData.start;
    const end = isWall ? currentWallData.baseData.end : currentWallData.end;
    const currentWallLength = Math.round(Point2dUtils.distanceTo(start, end));
    const legalScale = calValueScale(currentDoorWindowData.width, currentWallLength);
    const minScale = calValueScale(currentDoorWindowData.width, BUILD_PROPERTIES.MIN_INPUT);
    shadowScale = Math.max(Math.min(legalScale, shadowScale), minScale);
  }

  return shadowScale;
}

export function getModifyDoorWindowData(doorWindowId: string, behaviors: EParamsKeys[], value: number) {
  const doorWindowData = globalStores.modelStore.getWindowData(doorWindowId);
  if (doorWindowData) {
    const modifyData = cloneDeep(doorWindowData);
    // 更新模型
    let scale = 1;
    const len = behaviors.length;
    for (let i = 0; i < len; i++) {
      const behavior = behaviors[i];
      if (behavior === EParamsKeys.width) {
        if (i === 0) {
          scale = calValueScale(modifyData.width, value);
          scale = checkDoorWindowSizeScaleIsLegal(scale, behaviors, doorWindowData);
        }
        modifyData.width *= scale;
      } else if (behavior === EParamsKeys.height) {
        if (i === 0) {
          scale = calValueScale(modifyData.height, value);
          scale = checkDoorWindowSizeScaleIsLegal(scale, behaviors, doorWindowData);
        }
        modifyData.height *= scale;
      } else if (behavior === EParamsKeys.offGround) {
        modifyData.offGround = Math.round(value);
      }
    }

    return { originalData: doorWindowData, modifyData };
  }
  return undefined;
}

export function editDoorWindowData(doorWindowId: string, behaviors: EParamsKeys[], value: number) {
  const { originalData, modifyData } = getModifyDoorWindowData(doorWindowId, behaviors, value) ?? {};

  // @NOTE：加上了边界判断后再更新前要判断更新前后的数据是否一致，一致则不更新
  if (originalData && modifyData && !isEqual(originalData, modifyData)) {
    globalStores.windowDoorOperation.modifyWindowDoor(originalData.id, modifyData);
  }
}

export const resetDoorWindowSize = (doorWindowId: string, defaultWidth: number, defaultHeight: number) => {
  const doorWindowData = globalStores.modelStore.getWindowData(doorWindowId);
  if (doorWindowData) {
    const modifyData = cloneDeep(doorWindowData);
    modifyData.width = defaultWidth;
    modifyData.height = defaultHeight;
    if (!isEqual(doorWindowData, modifyData)) {
      globalStores.windowDoorOperation.modifyWindowDoor(doorWindowData.id, modifyData);
    }
  }
};

export function editColumnData(columnId: string, behaviors: EParamsKeys[], value: number) {
  const columnData = globalStores.structStore.getColumnData(columnId);
  if (columnData) {
    const modifyData = cloneDeep(columnData);
    const isRectColumn = modifyData.type === EColumnType.squareColumn;
    // 更新模型
    let scale = 1;
    const len = behaviors.length;
    for (let i = 0; i < len; i++) {
      const behavior = behaviors[i];
      if (behavior === EParamsKeys.length && isRectColumn && modifyData.width) {
        if (i === 0) {
          scale = calValueScale(modifyData.width, value);
        }
        modifyData.width *= scale;
      } else if (behavior === EParamsKeys.width && isRectColumn && modifyData.thickness) {
        if (i === 0) {
          scale = calValueScale(modifyData.thickness, value);
        }
        modifyData.thickness *= scale;
      } else if (behavior === EParamsKeys.rotation) {
        modifyData.rotation = (value * Math.PI) / 180;
      } else if (behavior === EParamsKeys.diameter && !isRectColumn) {
        modifyData.radius = value / 2;
      }
    }

    modifyData.id = columnData.id;
    globalStores.structOperation.modifyColumn(modifyData.id, modifyData);
    modelManager.selectedRerender();
  }
}

export function editVirtualColumnData(behaviors: EParamsKeys[], value: number) {
  const virtualColumnData = globalStores.editorStateStore.columnVirtualData;
  if (virtualColumnData) {
    // 更新模型
    let scale = 1;
    const len = behaviors.length;
    const isRectColumn = virtualColumnData.type === EColumnType.squareColumn;
    for (let i = 0; i < len; i++) {
      const behavior = behaviors[i];
      if (behavior === EParamsKeys.length && isRectColumn && virtualColumnData.width) {
        if (i === 0) {
          scale = calValueScale(virtualColumnData.width, value);
        }
        virtualColumnData.width *= scale;
      } else if (behavior === EParamsKeys.width && isRectColumn && virtualColumnData.thickness) {
        if (i === 0) {
          scale = calValueScale(virtualColumnData.thickness, value);
        }
        virtualColumnData.thickness *= scale;
      } else if (behavior === EParamsKeys.rotation) {
        virtualColumnData.rotation = (value * Math.PI) / 180;
      } else if (behavior === EParamsKeys.diameter && !isRectColumn) {
        virtualColumnData.radius = value / 2;
      }
    }

    virtualColumnData.id = getUniqueId();
  }
}

export function setEditFurnitureEntity(furnitureId: string, behaviors: EParamsKeys[], value: number) {
  const furnitureData = globalStores.modelStore.getFurnitureData(furnitureId);
  if (furnitureData) {
    const data = cloneDeep(furnitureData);
    let scale = 1;
    const len = behaviors.length;
    for (let i = 0; i < len; i++) {
      const behavior = behaviors[i];
      switch (behavior) {
        case EParamsKeys.length:
          if (data.thickness) {
            if (i === 0) {
              scale = clampFurnitureScale(calValueScale(data.thickness, value), behaviors, furnitureData);
            }
            data.thickness = Math.round(data.thickness * scale);
          }
          break;
        case EParamsKeys.width:
          if (data.width) {
            if (i === 0) {
              scale = clampFurnitureScale(calValueScale(data.width, value), behaviors, furnitureData);
            }
            data.width = Math.round(data.width * scale);
          }
          break;
        case EParamsKeys.height:
          if (data.height) {
            if (i === 0) {
              scale = clampFurnitureScale(calValueScale(data.height, value), behaviors, furnitureData);
            }
            data.height = Math.round(data.height * scale);
          }
          break;
        case EParamsKeys.rotation: {
          data.rotation = (Math.PI / 180) * value;
          break;
        }
        case EParamsKeys.offGround: {
          data.offGround = value;
          break;
        }
      }
    }

    globalStores.furnitureOperation.modifyFurniture(data.id, data);
    modelManager.selectedRerender();
  }
}

/**
 * 放置中的虚拟组合的参数修改。
 * 组合的尺寸在面板上是只读的（由配件的包围盒推出），因此这里只处理旋转与离地
 */
export function editVirtualAssemblyData(behaviors: EParamsKeys[], value: number) {
  const virtualAssemblyData = globalStores.editorStateStore.assemblyVirtualData;
  if (!virtualAssemblyData) return;

  const data = cloneDeep(virtualAssemblyData);
  for (const behavior of behaviors) {
    switch (behavior) {
      case EParamsKeys.rotation: {
        data.rotation = (Math.PI / 180) * value;
        break;
      }
      case EParamsKeys.offGround: {
        data.offGround = value;
        break;
      }
    }
  }

  globalStores.editorStateStore.assemblyVirtualData = data;
}

export function editVirtualFurnitureData(behaviors: EParamsKeys[], value: number) {
  const virtualFurnitureData = globalStores.editorStateStore.furnitureVirtualData;
  if (virtualFurnitureData) {
    const data = cloneDeep(virtualFurnitureData);
    let scale = 1;
    const len = behaviors.length;
    for (let i = 0; i < len; i++) {
      const behavior = behaviors[i];
      switch (behavior) {
        case EParamsKeys.length:
          if (data.thickness) {
            if (i === 0) {
              scale = clampFurnitureScale(calValueScale(data.thickness, value), behaviors, virtualFurnitureData);
            }
            data.thickness = Math.round(data.thickness * scale);
          }
          break;
        case EParamsKeys.width:
          if (data.width) {
            if (i === 0) {
              scale = clampFurnitureScale(calValueScale(data.width, value), behaviors, virtualFurnitureData);
            }
            data.width = Math.round(data.width * scale);
          }
          break;
        case EParamsKeys.height:
          if (data.height) {
            if (i === 0) {
              scale = clampFurnitureScale(calValueScale(data.height, value), behaviors, virtualFurnitureData);
            }
            data.height = Math.round(data.height * scale);
          }
          break;
        case EParamsKeys.rotation: {
          data.rotation = (Math.PI / 180) * value;
          break;
        }
        case EParamsKeys.offGround: {
          data.offGround = value;
          break;
        }
      }
    }

    globalStores.editorStateStore.furnitureVirtualData = data;
  }
}
